import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Mail,
  Phone,
  FileText,
  Download,
  ChevronDown,
  ChevronRight,
  Settings,
  FileSpreadsheet,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { KPI } from "@/components/ui/kpi";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { MultiSelect } from "@/components/ui/multiselect";
import { toast } from "sonner";
import { formatDate, formatCurrency, formatWholeNumber } from "@/lib/utils";
import { AllContactsTab } from "@/components/customers/all-contacts-tab";
import { CommunicationHistoryTab } from "@/components/customers/communication-history-tab";
import { ColumnConfigDialog } from "@/components/customers/column-config-dialog";
import { useCustomerColumnPreferences } from "@/hooks/useCustomerColumnPreferences";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCustomersPageData } from "@/hooks/useCustomersData";
import type { CustomersKpisUI } from "@/types/ui/customers";

// Types
interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: "high" | "medium" | "low";
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  collectionStrategyId: string;
}

interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "overdue" | "outstanding" | "paid";
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Data - 56 Customers
const initialCustomers: Customer[] = [
  // High Risk Customers (12)
  {
    id: "1",
    name: "Acme Corporation",
    accountNumber: "ACC-001",
    currency: "USD",
    outstanding: 214000,
    overdue: 214000,
    creditLimit: 250000,
    dso: 95,
    risk: "high",
    lastPayment: "2023-11-15",
    contact: "john.doe@acme.com",
    phone: "+1 (555) 123-4567",
    address: "123 Business St, New York, NY 10001",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "2",
    name: "Global Tech Industries",
    accountNumber: "ACC-002",
    currency: "USD",
    outstanding: 198000,
    overdue: 198000,
    creditLimit: 300000,
    dso: 102,
    risk: "high",
    lastPayment: "2023-10-20",
    contact: "sarah.smith@globaltech.com",
    phone: "+1 (555) 234-5678",
    address: "456 Tech Ave, San Francisco, CA 94102",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "3",
    name: "Mega Retail Group",
    accountNumber: "ACC-003",
    currency: "EUR",
    outstanding: 187000,
    overdue: 187000,
    creditLimit: 200000,
    dso: 88,
    risk: "high",
    lastPayment: "2023-12-01",
    contact: "michael.brown@megaretail.eu",
    phone: "+44 20 7123 4567",
    address: "789 Retail Rd, London, UK",
    paymentTerms: "Net 45",
    collectionStrategyId: "2",
  },
  {
    id: "4",
    name: "Premier Manufacturing",
    accountNumber: "ACC-004",
    currency: "USD",
    outstanding: 176000,
    overdue: 176000,
    creditLimit: 220000,
    dso: 91,
    risk: "high",
    lastPayment: "2023-11-28",
    contact: "lisa.johnson@premier.com",
    phone: "+1 (555) 345-6789",
    address: "321 Factory Ln, Detroit, MI 48201",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "5",
    name: "Sunrise Enterprises",
    accountNumber: "ACC-005",
    currency: "GBP",
    outstanding: 165000,
    overdue: 165000,
    creditLimit: 180000,
    dso: 97,
    risk: "high",
    lastPayment: "2023-11-10",
    contact: "david.wilson@sunrise.co.uk",
    phone: "+44 20 8123 4567",
    address: "654 Enterprise Way, Manchester, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "6",
    name: "Quantum Solutions",
    accountNumber: "ACC-006",
    currency: "USD",
    outstanding: 154000,
    overdue: 154000,
    creditLimit: 200000,
    dso: 85,
    risk: "high",
    lastPayment: "2023-12-05",
    contact: "emily.davis@quantum.com",
    phone: "+1 (555) 456-7890",
    address: "987 Innovation Dr, Austin, TX 78701",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "7",
    name: "Atlantic Trading Co",
    accountNumber: "ACC-007",
    currency: "EUR",
    outstanding: 143000,
    overdue: 143000,
    creditLimit: 160000,
    dso: 93,
    risk: "high",
    lastPayment: "2023-11-18",
    contact: "robert.miller@atlantic.eu",
    phone: "+33 1 42 12 34 56",
    address: "234 Commerce Blvd, Paris, France",
    paymentTerms: "Net 45",
    collectionStrategyId: "2",
  },
  {
    id: "8",
    name: "Pacific Imports",
    accountNumber: "ACC-008",
    currency: "AUD",
    outstanding: 132000,
    overdue: 132000,
    creditLimit: 150000,
    dso: 89,
    risk: "high",
    lastPayment: "2023-11-25",
    contact: "jennifer.taylor@pacific.au",
    phone: "+61 2 9123 4567",
    address: "567 Harbor St, Sydney, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "9",
    name: "Northern Logistics",
    accountNumber: "ACC-009",
    currency: "CAD",
    outstanding: 121000,
    overdue: 121000,
    creditLimit: 140000,
    dso: 86,
    risk: "high",
    lastPayment: "2023-12-02",
    contact: "william.anderson@northern.ca",
    phone: "+1 (416) 123-4567",
    address: "890 Transport Ave, Toronto, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "10",
    name: "Summit Distribution",
    accountNumber: "ACC-010",
    currency: "USD",
    outstanding: 110000,
    overdue: 110000,
    creditLimit: 130000,
    dso: 92,
    risk: "high",
    lastPayment: "2023-11-22",
    contact: "mary.thomas@summit.com",
    phone: "+1 (555) 567-8901",
    address: "123 Distribution Way, Denver, CO 80202",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "11",
    name: "Coastal Ventures",
    accountNumber: "ACC-011",
    currency: "GBP",
    outstanding: 99000,
    overdue: 99000,
    creditLimit: 120000,
    dso: 84,
    risk: "high",
    lastPayment: "2023-12-08",
    contact: "james.jackson@coastal.co.uk",
    phone: "+44 20 9123 4567",
    address: "456 Seaside Rd, Brighton, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  {
    id: "12",
    name: "Metro Services Group",
    accountNumber: "ACC-012",
    currency: "USD",
    outstanding: 88000,
    overdue: 88000,
    creditLimit: 110000,
    dso: 87,
    risk: "high",
    lastPayment: "2023-11-30",
    contact: "patricia.white@metro.com",
    phone: "+1 (555) 678-9012",
    address: "789 Metro Plaza, Chicago, IL 60601",
    paymentTerms: "Net 30",
    collectionStrategyId: "2",
  },
  // Medium Risk Customers (20)
  {
    id: "13",
    name: "Horizon Technologies",
    accountNumber: "ACC-013",
    currency: "USD",
    outstanding: 145000,
    overdue: 72500,
    creditLimit: 200000,
    dso: 65,
    risk: "medium",
    lastPayment: "2024-01-10",
    contact: "christopher.harris@horizon.com",
    phone: "+1 (555) 789-0123",
    address: "321 Tech Park, Seattle, WA 98101",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "14",
    name: "Silverline Industries",
    accountNumber: "ACC-014",
    currency: "EUR",
    outstanding: 138000,
    overdue: 69000,
    creditLimit: 180000,
    dso: 62,
    risk: "medium",
    lastPayment: "2024-01-15",
    contact: "barbara.martin@silverline.eu",
    phone: "+49 30 1234 5678",
    address: "654 Industrial St, Berlin, Germany",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "15",
    name: "Apex Commercial",
    accountNumber: "ACC-015",
    currency: "USD",
    outstanding: 127000,
    overdue: 63500,
    creditLimit: 170000,
    dso: 58,
    risk: "medium",
    lastPayment: "2024-01-20",
    contact: "daniel.thompson@apex.com",
    phone: "+1 (555) 890-1234",
    address: "987 Commerce Dr, Boston, MA 02101",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "16",
    name: "Pinnacle Systems",
    accountNumber: "ACC-016",
    currency: "GBP",
    outstanding: 116000,
    overdue: 58000,
    creditLimit: 150000,
    dso: 55,
    risk: "medium",
    lastPayment: "2024-01-25",
    contact: "nancy.garcia@pinnacle.co.uk",
    phone: "+44 20 1234 5678",
    address: "234 Systems Ave, Birmingham, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "17",
    name: "Vertex Corporation",
    accountNumber: "ACC-017",
    currency: "USD",
    outstanding: 105000,
    overdue: 52500,
    creditLimit: 140000,
    dso: 52,
    risk: "medium",
    lastPayment: "2024-02-01",
    contact: "matthew.martinez@vertex.com",
    phone: "+1 (555) 901-2345",
    address: "567 Corporate Blvd, Atlanta, GA 30301",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "18",
    name: "Fusion Enterprises",
    accountNumber: "ACC-018",
    currency: "CAD",
    outstanding: 94000,
    overdue: 47000,
    creditLimit: 130000,
    dso: 49,
    risk: "medium",
    lastPayment: "2024-02-05",
    contact: "karen.robinson@fusion.ca",
    phone: "+1 (604) 123-4567",
    address: "890 Enterprise Way, Vancouver, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "19",
    name: "Nexus Trading",
    accountNumber: "ACC-019",
    currency: "EUR",
    outstanding: 83000,
    overdue: 41500,
    creditLimit: 120000,
    dso: 46,
    risk: "medium",
    lastPayment: "2024-02-10",
    contact: "steven.clark@nexus.eu",
    phone: "+31 20 123 4567",
    address: "123 Trade Center, Amsterdam, Netherlands",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "20",
    name: "Catalyst Solutions",
    accountNumber: "ACC-020",
    currency: "USD",
    outstanding: 72000,
    overdue: 36000,
    creditLimit: 110000,
    dso: 43,
    risk: "medium",
    lastPayment: "2024-02-15",
    contact: "betty.rodriguez@catalyst.com",
    phone: "+1 (555) 012-3456",
    address: "456 Solution St, Phoenix, AZ 85001",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "21",
    name: "Zenith Group",
    accountNumber: "ACC-021",
    currency: "AUD",
    outstanding: 61000,
    overdue: 30500,
    creditLimit: 100000,
    dso: 40,
    risk: "medium",
    lastPayment: "2024-02-20",
    contact: "donald.lewis@zenith.au",
    phone: "+61 3 9123 4567",
    address: "789 Business Park, Melbourne, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "22",
    name: "Vanguard Industries",
    accountNumber: "ACC-022",
    currency: "USD",
    outstanding: 125000,
    overdue: 62500,
    creditLimit: 160000,
    dso: 67,
    risk: "medium",
    lastPayment: "2024-01-12",
    contact: "dorothy.lee@vanguard.com",
    phone: "+1 (555) 123-4567",
    address: "321 Industrial Pkwy, Pittsburgh, PA 15201",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "23",
    name: "Omega Systems",
    accountNumber: "ACC-023",
    currency: "GBP",
    outstanding: 114000,
    overdue: 57000,
    creditLimit: 150000,
    dso: 64,
    risk: "medium",
    lastPayment: "2024-01-18",
    contact: "joshua.walker@omega.co.uk",
    phone: "+44 20 2345 6789",
    address: "654 Tech Quarter, Leeds, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "24",
    name: "Titan Commercial",
    accountNumber: "ACC-024",
    currency: "EUR",
    outstanding: 103000,
    overdue: 51500,
    creditLimit: 140000,
    dso: 61,
    risk: "medium",
    lastPayment: "2024-01-22",
    contact: "sandra.hall@titan.eu",
    phone: "+34 91 123 4567",
    address: "987 Commercial Ave, Madrid, Spain",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "25",
    name: "Eclipse Trading",
    accountNumber: "ACC-025",
    currency: "USD",
    outstanding: 92000,
    overdue: 46000,
    creditLimit: 130000,
    dso: 58,
    risk: "medium",
    lastPayment: "2024-01-28",
    contact: "ashley.allen@eclipse.com",
    phone: "+1 (555) 234-5678",
    address: "234 Trade Plaza, Miami, FL 33101",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "26",
    name: "Meridian Group",
    accountNumber: "ACC-026",
    currency: "CAD",
    outstanding: 81000,
    overdue: 40500,
    creditLimit: 120000,
    dso: 55,
    risk: "medium",
    lastPayment: "2024-02-03",
    contact: "kimberly.young@meridian.ca",
    phone: "+1 (403) 123-4567",
    address: "567 Group Center, Calgary, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "27",
    name: "Spectrum Solutions",
    accountNumber: "ACC-027",
    currency: "USD",
    outstanding: 70000,
    overdue: 35000,
    creditLimit: 110000,
    dso: 52,
    risk: "medium",
    lastPayment: "2024-02-08",
    contact: "mark.hernandez@spectrum.com",
    phone: "+1 (555) 345-6789",
    address: "890 Solution Way, Portland, OR 97201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "28",
    name: "Prism Enterprises",
    accountNumber: "ACC-028",
    currency: "GBP",
    outstanding: 118000,
    overdue: 59000,
    creditLimit: 155000,
    dso: 69,
    risk: "medium",
    lastPayment: "2024-01-08",
    contact: "donna.king@prism.co.uk",
    phone: "+44 20 3456 7890",
    address: "123 Enterprise Rd, Glasgow, UK",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "29",
    name: "Cascade Industries",
    accountNumber: "ACC-029",
    currency: "EUR",
    outstanding: 107000,
    overdue: 53500,
    creditLimit: 145000,
    dso: 66,
    risk: "medium",
    lastPayment: "2024-01-14",
    contact: "paul.wright@cascade.eu",
    phone: "+41 22 123 4567",
    address: "456 Industrial Zone, Geneva, Switzerland",
    paymentTerms: "Net 45",
    collectionStrategyId: "1",
  },
  {
    id: "30",
    name: "Stellar Corporation",
    accountNumber: "ACC-030",
    currency: "USD",
    outstanding: 96000,
    overdue: 48000,
    creditLimit: 135000,
    dso: 63,
    risk: "medium",
    lastPayment: "2024-01-19",
    contact: "carol.lopez@stellar.com",
    phone: "+1 (555) 456-7890",
    address: "789 Corporate Center, Dallas, TX 75201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "31",
    name: "Quantum Dynamics",
    accountNumber: "ACC-031",
    currency: "AUD",
    outstanding: 85000,
    overdue: 42500,
    creditLimit: 125000,
    dso: 60,
    risk: "medium",
    lastPayment: "2024-01-24",
    contact: "george.hill@quantum.au",
    phone: "+61 7 9123 4567",
    address: "321 Dynamics Dr, Brisbane, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "32",
    name: "Nexus Ventures",
    accountNumber: "ACC-032",
    currency: "USD",
    outstanding: 74000,
    overdue: 37000,
    creditLimit: 115000,
    dso: 57,
    risk: "medium",
    lastPayment: "2024-01-30",
    contact: "ruth.scott@nexusv.com",
    phone: "+1 (555) 567-8901",
    address: "654 Venture Blvd, San Diego, CA 92101",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  // Low Risk Customers (24)
  {
    id: "33",
    name: "Bright Future Inc",
    accountNumber: "ACC-033",
    currency: "USD",
    outstanding: 89000,
    overdue: 0,
    creditLimit: 150000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "frank.green@brightfuture.com",
    phone: "+1 (555) 678-9012",
    address: "987 Future Ave, Minneapolis, MN 55401",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "34",
    name: "Swift Solutions",
    accountNumber: "ACC-034",
    currency: "EUR",
    outstanding: 78000,
    overdue: 0,
    creditLimit: 130000,
    dso: 25,
    risk: "low",
    lastPayment: "2024-03-05",
    contact: "virginia.adams@swift.eu",
    phone: "+32 2 123 4567",
    address: "234 Solution St, Brussels, Belgium",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "35",
    name: "Reliable Trading Co",
    accountNumber: "ACC-035",
    currency: "GBP",
    outstanding: 67000,
    overdue: 0,
    creditLimit: 120000,
    dso: 22,
    risk: "low",
    lastPayment: "2024-03-08",
    contact: "henry.baker@reliable.co.uk",
    phone: "+44 20 4567 8901",
    address: "567 Trade Center, Edinburgh, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "36",
    name: "Premier Partners",
    accountNumber: "ACC-036",
    currency: "USD",
    outstanding: 56000,
    overdue: 0,
    creditLimit: 100000,
    dso: 19,
    risk: "low",
    lastPayment: "2024-03-10",
    contact: "frances.gonzalez@premier.com",
    phone: "+1 (555) 789-0123",
    address: "890 Partner Plaza, Nashville, TN 37201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "37",
    name: "Excellence Corp",
    accountNumber: "ACC-037",
    currency: "CAD",
    outstanding: 95000,
    overdue: 4750,
    creditLimit: 140000,
    dso: 32,
    risk: "low",
    lastPayment: "2024-02-28",
    contact: "roy.nelson@excellence.ca",
    phone: "+1 (514) 123-4567",
    address: "123 Excellence Way, Montreal, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "38",
    name: "Integrity Systems",
    accountNumber: "ACC-038",
    currency: "USD",
    outstanding: 84000,
    overdue: 4200,
    creditLimit: 130000,
    dso: 29,
    risk: "low",
    lastPayment: "2024-03-02",
    contact: "irene.carter@integrity.com",
    phone: "+1 (555) 890-1234",
    address: "456 Integrity Dr, Columbus, OH 43201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "39",
    name: "Quality Enterprises",
    accountNumber: "ACC-039",
    currency: "EUR",
    outstanding: 73000,
    overdue: 3650,
    creditLimit: 120000,
    dso: 26,
    risk: "low",
    lastPayment: "2024-03-04",
    contact: "randy.mitchell@quality.eu",
    phone: "+43 1 123 4567",
    address: "789 Quality Blvd, Vienna, Austria",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "40",
    name: "Trustworthy Trading",
    accountNumber: "ACC-040",
    currency: "GBP",
    outstanding: 62000,
    overdue: 3100,
    creditLimit: 110000,
    dso: 23,
    risk: "low",
    lastPayment: "2024-03-06",
    contact: "marilyn.perez@trustworthy.co.uk",
    phone: "+44 20 5678 9012",
    address: "321 Trust Ave, Cardiff, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "41",
    name: "Precision Industries",
    accountNumber: "ACC-041",
    currency: "USD",
    outstanding: 91000,
    overdue: 0,
    creditLimit: 135000,
    dso: 30,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "arthur.roberts@precision.com",
    phone: "+1 (555) 901-2345",
    address: "654 Precision Way, Indianapolis, IN 46201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "42",
    name: "Efficient Solutions",
    accountNumber: "ACC-042",
    currency: "AUD",
    outstanding: 80000,
    overdue: 0,
    creditLimit: 125000,
    dso: 27,
    risk: "low",
    lastPayment: "2024-03-03",
    contact: "evelyn.turner@efficient.au",
    phone: "+61 8 9123 4567",
    address: "987 Efficiency Dr, Perth, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "43",
    name: "Dependable Corp",
    accountNumber: "ACC-043",
    currency: "USD",
    outstanding: 69000,
    overdue: 0,
    creditLimit: 115000,
    dso: 24,
    risk: "low",
    lastPayment: "2024-03-07",
    contact: "harold.phillips@dependable.com",
    phone: "+1 (555) 012-3456",
    address: "234 Dependable St, Charlotte, NC 28201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "44",
    name: "Solid Ventures",
    accountNumber: "ACC-044",
    currency: "CAD",
    outstanding: 58000,
    overdue: 0,
    creditLimit: 105000,
    dso: 21,
    risk: "low",
    lastPayment: "2024-03-09",
    contact: "mildred.campbell@solid.ca",
    phone: "+1 (780) 123-4567",
    address: "567 Venture Park, Edmonton, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "45",
    name: "Stable Systems",
    accountNumber: "ACC-045",
    currency: "EUR",
    outstanding: 87000,
    overdue: 4350,
    creditLimit: 130000,
    dso: 31,
    risk: "low",
    lastPayment: "2024-02-29",
    contact: "clarence.parker@stable.eu",
    phone: "+45 33 12 34 56",
    address: "890 Stable Way, Copenhagen, Denmark",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "46",
    name: "Consistent Trading",
    accountNumber: "ACC-046",
    currency: "GBP",
    outstanding: 76000,
    overdue: 3800,
    creditLimit: 120000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-02",
    contact: "carl.evans@consistent.co.uk",
    phone: "+44 20 6789 0123",
    address: "123 Consistent Rd, Liverpool, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "47",
    name: "Steady Enterprises",
    accountNumber: "ACC-047",
    currency: "USD",
    outstanding: 65000,
    overdue: 3250,
    creditLimit: 110000,
    dso: 25,
    risk: "low",
    lastPayment: "2024-03-05",
    contact: "willie.edwards@steady.com",
    phone: "+1 (555) 123-4567",
    address: "456 Steady Ave, Milwaukee, WI 53201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "48",
    name: "Secure Solutions",
    accountNumber: "ACC-048",
    currency: "USD",
    outstanding: 54000,
    overdue: 0,
    creditLimit: 100000,
    dso: 18,
    risk: "low",
    lastPayment: "2024-03-11",
    contact: "lois.collins@secure.com",
    phone: "+1 (555) 234-5678",
    address: "789 Secure Blvd, Las Vegas, NV 89101",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "49",
    name: "Prompt Partners",
    accountNumber: "ACC-049",
    currency: "EUR",
    outstanding: 93000,
    overdue: 0,
    creditLimit: 135000,
    dso: 33,
    risk: "low",
    lastPayment: "2024-02-27",
    contact: "earl.stewart@prompt.eu",
    phone: "+46 8 123 4567",
    address: "321 Prompt Plaza, Stockholm, Sweden",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "50",
    name: "Swift Industries",
    accountNumber: "ACC-050",
    currency: "AUD",
    outstanding: 82000,
    overdue: 0,
    creditLimit: 125000,
    dso: 30,
    risk: "low",
    lastPayment: "2024-03-01",
    contact: "judith.sanchez@swifti.au",
    phone: "+61 2 8123 4567",
    address: "654 Swift Way, Adelaide, Australia",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "51",
    name: "Rapid Response Co",
    accountNumber: "ACC-051",
    currency: "USD",
    outstanding: 71000,
    overdue: 0,
    creditLimit: 115000,
    dso: 27,
    risk: "low",
    lastPayment: "2024-03-04",
    contact: "frederick.morris@rapid.com",
    phone: "+1 (555) 345-6789",
    address: "987 Response Rd, Baltimore, MD 21201",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "52",
    name: "Timely Trading",
    accountNumber: "ACC-052",
    currency: "CAD",
    outstanding: 60000,
    overdue: 0,
    creditLimit: 105000,
    dso: 24,
    risk: "low",
    lastPayment: "2024-03-07",
    contact: "joyce.rogers@timely.ca",
    phone: "+1 (204) 123-4567",
    address: "234 Timely Center, Winnipeg, Canada",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "53",
    name: "Punctual Systems",
    accountNumber: "ACC-053",
    currency: "GBP",
    outstanding: 88000,
    overdue: 4400,
    creditLimit: 130000,
    dso: 34,
    risk: "low",
    lastPayment: "2024-02-26",
    contact: "gerald.reed@punctual.co.uk",
    phone: "+44 20 7890 1234",
    address: "567 Punctual Ave, Bristol, UK",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "54",
    name: "Expedite Enterprises",
    accountNumber: "ACC-054",
    currency: "EUR",
    outstanding: 77000,
    overdue: 3850,
    creditLimit: 120000,
    dso: 31,
    risk: "low",
    lastPayment: "2024-02-29",
    contact: "alice.cook@expedite.eu",
    phone: "+47 22 12 34 56",
    address: "890 Expedite Way, Oslo, Norway",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "55",
    name: "Agile Corporation",
    accountNumber: "ACC-055",
    currency: "USD",
    outstanding: 66000,
    overdue: 3300,
    creditLimit: 110000,
    dso: 28,
    risk: "low",
    lastPayment: "2024-03-03",
    contact: "teresa.morgan@agile.com",
    phone: "+1 (555) 456-7890",
    address: "123 Agile Blvd, Sacramento, CA 95814",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
  {
    id: "56",
    name: "Dynamic Solutions",
    accountNumber: "ACC-056",
    currency: "USD",
    outstanding: 55000,
    overdue: 0,
    creditLimit: 100000,
    dso: 20,
    risk: "low",
    lastPayment: "2024-03-10",
    contact: "joe.bell@dynamic.com",
    phone: "+1 (555) 567-8901",
    address: "456 Dynamic Dr, Kansas City, MO 64101",
    paymentTerms: "Net 30",
    collectionStrategyId: "1",
  },
];

// Mock Invoices
const mockInvoices: Record<string, Invoice[]> = {
  "1": [
    {
      id: "INV-001",
      date: "2023-11-01",
      dueDate: "2023-12-01",
      amount: 76000,
      status: "overdue",
    },
    {
      id: "INV-002",
      date: "2023-11-15",
      dueDate: "2023-12-15",
      amount: 68000,
      status: "overdue",
    },
    {
      id: "INV-003",
      date: "2023-12-01",
      dueDate: "2023-12-31",
      amount: 70000,
      status: "overdue",
    },
  ],
};

// Mock Payments
const mockPayments: Record<string, Payment[]> = {
  "1": [
    {
      id: "PAY-001",
      date: "2023-11-15",
      amount: 50000,
      method: "Wire Transfer",
      reference: "WT-2023-001",
      allocations: [
        { invoiceId: "INV-001", invoiceDate: "2023-10-01", amount: 30000 },
        { invoiceId: "INV-002", invoiceDate: "2023-10-15", amount: 20000 },
      ],
    },
    {
      id: "PAY-002",
      date: "2023-10-20",
      amount: 35000,
      method: "ACH",
      reference: "ACH-2023-002",
      allocations: [{ invoiceId: "INV-003", invoiceDate: "2023-09-01", amount: 35000 }],
    },
  ],
};

// Collection Strategies
const collectionStrategies = [
  { id: "1", name: "Standard Collection Flow", status: "active" },
  { id: "2", name: "Intensive Collection", status: "active" },
  { id: "3", name: "Gentle Reminder Flow", status: "inactive" },
];

export function CustomersPage() {
  const navigate = useNavigate();
  
  // Fetch customers data using mode-aware hook
  const { customers, kpis, isLoading: loading, error } = useCustomersPageData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  // Dialog states
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [sendEmailDialog, setSendEmailDialog] = useState(false);
  const [logCallDialog, setLogCallDialog] = useState(false);
  const [addCustomerDialog, setAddCustomerDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // Form states
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Add customer form
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    accountNumber: "",
    contact: "",
    phone: "",
    address: "",
    currency: "USD" as "USD" | "EUR" | "GBP" | "CAD" | "AUD",
    creditLimit: "",
    paymentTerms: "Net 30" as "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90",
    collectionStrategyId: "",
  });

  // Payment expansion state
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    currencies: [] as string[],
    paymentTerms: [] as string[],
    collectionStrategies: [] as string[],
    outstandingMin: "",
    outstandingMax: "",
    overdueMin: "",
    overdueMax: "",
    creditLimitMin: "",
    creditLimitMax: "",
    dsoMin: "",
    dsoMax: "",
  });

  // Column configuration
  const { visibleColumns, setVisibleColumns } = useCustomerColumnPreferences();
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Sorting state
  type SortField = "accountNumber" | "name" | "contact" | "currency" | "outstanding" | 
                   "overdue" | "creditLimit" | "dso" | "risk" | "lastPayment" | 
                   "phone" | "address" | "paymentTerms" | "collectionStrategy" | 
                   "availableCredit" | "creditUtilization";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    let result = customers.filter((customer) => {
      const matchesSearch =
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.accountNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRisk = riskFilter === "all" || customer.risk === riskFilter;

      // Advanced filters
      const matchesCurrency =
        advancedFilters.currencies.length === 0 ||
        advancedFilters.currencies.includes(customer.currency);

      const matchesPaymentTerms =
        advancedFilters.paymentTerms.length === 0 ||
        advancedFilters.paymentTerms.includes(customer.paymentTerms);

      const matchesCollectionStrategy =
        advancedFilters.collectionStrategies.length === 0 ||
        advancedFilters.collectionStrategies.includes(customer.collectionStrategyId);

      const matchesOutstandingMin =
        !advancedFilters.outstandingMin ||
        customer.outstanding >= parseFloat(advancedFilters.outstandingMin);

      const matchesOutstandingMax =
        !advancedFilters.outstandingMax ||
        customer.outstanding <= parseFloat(advancedFilters.outstandingMax);

      const matchesOverdueMin =
        !advancedFilters.overdueMin ||
        customer.overdue >= parseFloat(advancedFilters.overdueMin);

      const matchesOverdueMax =
        !advancedFilters.overdueMax ||
        customer.overdue <= parseFloat(advancedFilters.overdueMax);

      const matchesCreditLimitMin =
        !advancedFilters.creditLimitMin ||
        customer.creditLimit >= parseFloat(advancedFilters.creditLimitMin);

      const matchesCreditLimitMax =
        !advancedFilters.creditLimitMax ||
        customer.creditLimit <= parseFloat(advancedFilters.creditLimitMax);

      const matchesDsoMin =
        !advancedFilters.dsoMin ||
        customer.dso >= parseFloat(advancedFilters.dsoMin);

      const matchesDsoMax =
        !advancedFilters.dsoMax ||
        customer.dso <= parseFloat(advancedFilters.dsoMax);

      return matchesSearch &&
        matchesRisk &&
        matchesCurrency &&
        matchesPaymentTerms &&
        matchesCollectionStrategy &&
        matchesOutstandingMin &&
        matchesOutstandingMax &&
        matchesOverdueMin &&
        matchesOverdueMax &&
        matchesCreditLimitMin &&
        matchesCreditLimitMax &&
        matchesDsoMin &&
        matchesDsoMax;
    });

    // Apply sorting if a sort field is selected
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Custom risk order: high > medium > low
        const riskOrder = { high: 3, medium: 2, low: 1 };

        switch (sortField) {
          case "accountNumber":
            aValue = a.accountNumber.toLowerCase();
            bValue = b.accountNumber.toLowerCase();
            break;
          case "name":
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case "contact":
            aValue = a.contact.toLowerCase();
            bValue = b.contact.toLowerCase();
            break;
          case "currency":
            aValue = a.currency;
            bValue = b.currency;
            break;
          case "outstanding":
            aValue = a.outstanding;
            bValue = b.outstanding;
            break;
          case "overdue":
            aValue = a.overdue;
            bValue = b.overdue;
            break;
          case "creditLimit":
            aValue = a.creditLimit;
            bValue = b.creditLimit;
            break;
          case "dso":
            aValue = a.dso;
            bValue = b.dso;
            break;
          case "risk":
            aValue = riskOrder[a.risk as keyof typeof riskOrder];
            bValue = riskOrder[b.risk as keyof typeof riskOrder];
            break;
          case "lastPayment":
            aValue = new Date(a.lastPayment).getTime();
            bValue = new Date(b.lastPayment).getTime();
            break;
          case "phone":
            aValue = a.phone.toLowerCase();
            bValue = b.phone.toLowerCase();
            break;
          case "address":
            aValue = a.address.toLowerCase();
            bValue = b.address.toLowerCase();
            break;
          case "paymentTerms":
            aValue = a.paymentTerms;
            bValue = b.paymentTerms;
            break;
          case "collectionStrategy":
            aValue = a.collectionStrategyId || "";
            bValue = b.collectionStrategyId || "";
            break;
          case "availableCredit":
            aValue = a.creditLimit - a.outstanding;
            bValue = b.creditLimit - b.outstanding;
            break;
          case "creditUtilization":
            aValue = a.creditLimit > 0 ? (a.outstanding / a.creditLimit) * 100 : 0;
            bValue = b.creditLimit > 0 ? (b.outstanding / b.creditLimit) * 100 : 0;
            break;
          default:
            return 0;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [customers, searchQuery, riskFilter, advancedFilters, sortField, sortDirection]);

  // Get selected customer for floating menu
  const selectedCustomer_floatingMenu =
    selectedCustomers.length === 1
      ? customers.find((c) => c.id === selectedCustomers[0])
      : null;

  // Risk badge helper
  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case "high":
        return <Badge variant="destructive">High Risk</Badge>;
      case "medium":
        return (
          <Badge className="bg-amber-500 text-white hover:bg-amber-600">Medium Risk</Badge>
        );
      case "low":
        return <Badge className="bg-green-600 text-white hover:bg-green-700">Low Risk</Badge>;
    }
  };

  // Column rendering helpers
  const getColumnHeader = (columnId: string) => {
    const headers: Record<string, { label: string; className?: string }> = {
      accountNumber: { label: "Account #" },
      name: { label: "Name" },
      contact: { label: "Contact Email" },
      currency: { label: "Currency" },
      outstanding: { label: "Outstanding", className: "text-right" },
      overdue: { label: "Overdue", className: "text-right" },
      creditLimit: { label: "Credit Limit", className: "text-right" },
      dso: { label: "DSO" },
      risk: { label: "Risk" },
      lastPayment: { label: "Last Payment" },
      phone: { label: "Phone" },
      address: { label: "Address" },
      paymentTerms: { label: "Payment Terms" },
      collectionStrategy: { label: "Collection Strategy" },
      availableCredit: { label: "Available Credit", className: "text-right" },
      creditUtilization: { label: "Credit Utilization %", className: "text-right" },
    };
    return headers[columnId] || { label: columnId };
  };

  const renderColumnCell = (columnId: string, customer: Customer) => {
    switch (columnId) {
      case "accountNumber":
        return <TableCell className="font-medium">{customer.accountNumber}</TableCell>;

      case "name":
        return (
          <TableCell>
            <button
              onClick={() => handleViewDetails(customer)}
              className="text-blue-600 hover:underline"
            >
              {customer.name}
            </button>
          </TableCell>
        );

      case "contact":
        return <TableCell>{customer.contact}</TableCell>;

      case "currency":
        return <TableCell>{customer.currency}</TableCell>;

      case "outstanding":
        return (
          <TableCell className="text-right">
            {formatCurrency(customer.outstanding, customer.currency)}
          </TableCell>
        );

      case "overdue":
        return (
          <TableCell className={`text-right ${customer.overdue > 0 ? "text-red-600" : ""}`}>
            {formatCurrency(customer.overdue, customer.currency)}
          </TableCell>
        );

      case "creditLimit":
        return (
          <TableCell className="text-right">
            {formatCurrency(customer.creditLimit, customer.currency)}
          </TableCell>
        );

      case "dso":
        return (
          <TableCell>
            <Badge variant={customer.dso > 60 ? "destructive" : "secondary"}>
              {customer.dso} days
            </Badge>
          </TableCell>
        );

      case "risk":
        return <TableCell>{getRiskBadge(customer.risk)}</TableCell>;

      case "lastPayment":
        return <TableCell>{formatDate(customer.lastPayment)}</TableCell>;

      case "phone":
        return <TableCell>{customer.phone}</TableCell>;

      case "address":
        return <TableCell className="max-w-[200px] truncate">{customer.address}</TableCell>;

      case "paymentTerms":
        return <TableCell>{customer.paymentTerms}</TableCell>;

      case "collectionStrategy":
        return (
          <TableCell>
            {customer.collectionStrategyId ? (
              <Badge variant="outline">{customer.collectionStrategyId}</Badge>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );

      case "availableCredit":
        const availableCredit = customer.creditLimit - customer.outstanding;
        return (
          <TableCell className="text-right">
            {formatCurrency(availableCredit, customer.currency)}
          </TableCell>
        );

      case "creditUtilization":
        const utilization = customer.creditLimit > 0
          ? ((customer.outstanding / customer.creditLimit) * 100).toFixed(1)
          : "0.0";
        return (
          <TableCell className="text-right">
            <Badge
              variant={parseFloat(utilization) > 80 ? "destructive" : parseFloat(utilization) > 60 ? "secondary" : "default"}
            >
              {utilization}%
            </Badge>
          </TableCell>
        );

      default:
        return <TableCell>-</TableCell>;
    }
  };

  // Handle checkbox selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCustomers(filteredCustomers.map((c) => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId: string, checked: boolean) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter((id) => id !== customerId));
    }
  };

  // Handle view details
  const handleViewDetails = (customer: Customer) => {
    navigate(`/customers/${customer.id}`, { state: { customer } });
  };

  // Handle send email
  const handleSendEmail = () => {
    if (!selectedCustomer_floatingMenu) return;

    const subject = `Payment Reminder - ${selectedCustomer_floatingMenu.accountNumber}`;
    const body = `Dear ${selectedCustomer_floatingMenu.name},

This is a friendly reminder regarding your outstanding balance.

Outstanding Amount: ${formatCurrency(selectedCustomer_floatingMenu.outstanding, selectedCustomer_floatingMenu.currency)}
Overdue Amount: ${formatCurrency(selectedCustomer_floatingMenu.overdue, selectedCustomer_floatingMenu.currency)}

Please contact us if you have any questions.

Best regards,
Accounts Receivable Team`;

    setEmailSubject(subject);
    setEmailBody(body);
    setSendEmailDialog(true);
  };

  // Handle log call
  const handleLogCall = () => {
    setLogCallDialog(true);
  };

  // Handle export statement
  const handleExportStatement = () => {
    if (selectedCustomers.length === 0) return;

    // Get all selected customer objects
    const selectedCustomerObjects = customers.filter((c) => selectedCustomers.includes(c.id));

    if (selectedCustomerObjects.length === 0) return;

    // Build CSV with header and all selected customers
    let csv = `Account Number,Name,Outstanding,Overdue,Credit Limit,DSO,Risk\n`;

    selectedCustomerObjects.forEach((customer) => {
      csv += `${customer.accountNumber},${customer.name},${customer.outstanding},${customer.overdue},${customer.creditLimit},${customer.dso},${customer.risk}\n`;
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    // Use different filename for single vs multiple customers
    if (selectedCustomerObjects.length === 1) {
      a.download = `statement-${selectedCustomerObjects[0].accountNumber}-${new Date().toISOString().split("T")[0]}.csv`;
    } else {
      a.download = `statements-bulk-${new Date().toISOString().split("T")[0]}.csv`;
    }

    a.click();

    const message = selectedCustomerObjects.length === 1
      ? "Statement exported successfully"
      : `${selectedCustomerObjects.length} statements exported successfully`;
    toast.success(message);
  };

  // Handle export to Excel
  const handleExportToExcel = () => {
    try {
      // Prepare data for export based on visible columns and filtered customers
      const exportData = filteredCustomers.map((customer) => {
        const row: Record<string, any> = {};

        visibleColumns.forEach((columnId) => {
          const header = getColumnHeader(columnId);

          switch (columnId) {
            case "accountNumber":
              row[header.label] = customer.accountNumber;
              break;
            case "name":
              row[header.label] = customer.name;
              break;
            case "contact":
              row[header.label] = customer.contact;
              break;
            case "currency":
              row[header.label] = customer.currency;
              break;
            case "outstanding":
              row[header.label] = customer.outstanding;
              break;
            case "overdue":
              row[header.label] = customer.overdue;
              break;
            case "creditLimit":
              row[header.label] = customer.creditLimit;
              break;
            case "dso":
              row[header.label] = customer.dso;
              break;
            case "risk":
              row[header.label] = customer.risk;
              break;
            case "lastPayment":
              row[header.label] = customer.lastPayment;
              break;
            case "phone":
              row[header.label] = customer.phone;
              break;
            case "address":
              row[header.label] = customer.address;
              break;
            case "paymentTerms":
              row[header.label] = customer.paymentTerms;
              break;
            case "collectionStrategy":
              row[header.label] = customer.collectionStrategyId || "None";
              break;
            case "availableCredit":
              row[header.label] = customer.creditLimit - customer.outstanding;
              break;
            case "creditUtilization":
              row[header.label] = customer.creditLimit > 0
                ? ((customer.outstanding / customer.creditLimit) * 100).toFixed(1) + "%"
                : "0.0%";
              break;
            default:
              row[header.label] = "";
          }
        });

        return row;
      });

      // Convert to CSV format
      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(","),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header];
            // Escape values that contain commas or quotes
            if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(",")
        )
      ].join("\n");

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customers-export-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Successfully exported ${exportData.length} customer records`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  // Handle save collection strategy
  const handleSaveCollectionStrategy = () => {
    if (!selectedCustomer) return;

    // Note: In a real implementation, this would call an API to update the customer
    // For now, just update the selected customer in the dialog
    setSelectedCustomer({ ...selectedCustomer, collectionStrategyId: editedCollectionStrategyId });

    toast.success("Collection strategy updated successfully");
  };

  // Handle add customer
  const handleAddCustomer = () => {
    // Validation
    if (!newCustomer.name || !newCustomer.accountNumber || !newCustomer.contact || !newCustomer.phone || !newCustomer.address || !newCustomer.creditLimit) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCustomer.contact)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Account number uniqueness
    if (customers.some((c) => c.accountNumber === newCustomer.accountNumber)) {
      toast.error("Account number already exists");
      return;
    }

    // Credit limit validation
    const creditLimit = parseFloat(newCustomer.creditLimit);
    if (isNaN(creditLimit) || creditLimit <= 0) {
      toast.error("Credit limit must be a positive number");
      return;
    }

    // Note: In a real implementation, this would call an API to create the customer
    // For now, just close the dialog and show success message
    setAddCustomerDialog(false);
    setNewCustomer({
      name: "",
      accountNumber: "",
      contact: "",
      phone: "",
      address: "",
      currency: "USD",
      creditLimit: "",
      paymentTerms: "Net 30",
      collectionStrategyId: "",
    });

    toast.success("Customer added successfully");
  };

  // Handle send email submit
  const handleSendEmailSubmit = () => {
    toast.success("Email sent successfully");
    setSendEmailDialog(false);
    setEmailSubject("");
    setEmailBody("");
  };

  // Handle log call submit
  const handleLogCallSubmit = () => {
    if (!callNotes || !callOutcome) {
      toast.error("Please fill in all fields");
      return;
    }

    toast.success("Call logged successfully");
    setLogCallDialog(false);
    setCallNotes("");
    setCallOutcome("");
  };

  // Calculate aging
  const calculateAging = (customer: Customer) => {
    const invoices = mockInvoices[customer.id] || [];
    const today = new Date();

    const aging = {
      current: 0,
      days1_30: 0,
      days31_60: 0,
      days61_90: 0,
      days90Plus: 0,
    };

    invoices.forEach((invoice) => {
      if (invoice.status === "paid") return;

      const dueDate = new Date(invoice.dueDate);
      const daysOverdue = Math.floor(
        (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysOverdue < 0) aging.current += invoice.amount;
      else if (daysOverdue <= 30) aging.days1_30 += invoice.amount;
      else if (daysOverdue <= 60) aging.days31_60 += invoice.amount;
      else if (daysOverdue <= 90) aging.days61_90 += invoice.amount;
      else aging.days90Plus += invoice.amount;
    });

    return aging;
  };

  // Toggle payment expansion
  const togglePaymentExpansion = (paymentId: string) => {
    if (expandedPayments.includes(paymentId)) {
      setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
    } else {
      setExpandedPayments([...expandedPayments, paymentId]);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Customer Accounts</h1>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-all hover:shadow-md border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-green-100">
                    <Users className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Total Customers
                </p>
                <p className="text-2xl font-bold">
                  {kpis.totalCustomers}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Active customer accounts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-orange-100">
                    <DollarSign className="h-4 w-4 text-orange-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Outstanding Balance
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(kpis.totalOutstanding, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Total across all customers
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-red-100">
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Overdue Balance
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(kpis.totalOverdue, "USD")}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Requires immediate attention
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded-full bg-red-100">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  High Risk Customers
                </p>
                <p className="text-2xl font-bold">
                  {kpis.highRiskCount}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Customers requiring monitoring
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Customers</CardTitle>
            <div className="flex items-center gap-2">
              {/* Action Buttons - Shown when customer is selected */}
              {selectedCustomer_floatingMenu && (
                <>
                  <Button
                    onClick={() => handleViewDetails(selectedCustomer_floatingMenu)}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    View Invoices
                  </Button>
                  <Button
                    onClick={handleSendEmail}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Send Email
                  </Button>
                  <Button
                    onClick={handleLogCall}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Log Call
                  </Button>
                </>
              )}
              {/* Send Statements button - Shown when one or more customers are selected */}
              {selectedCustomers.length > 0 && (
                <Button
                  onClick={handleExportStatement}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Send Statements
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleExportToExcel}
                      className="h-8 w-8"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export to Excel</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      className="h-8 w-8"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or account number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setMoreFiltersOpen(true)}>
              <Filter className="mr-2 h-4 w-4" />
              More Filters
            </Button>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={
                        filteredCustomers.length > 0 &&
                        selectedCustomers.length === filteredCustomers.length
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.map((columnId) => {
                    const header = getColumnHeader(columnId);
                    return (
                      <TableHead key={columnId} className={header.className}>
                        {header.label}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading customers...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-64 text-center">
                      <p className="text-sm text-muted-foreground">No customers found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCustomers.includes(customer.id)}
                          onCheckedChange={(checked) =>
                            handleSelectCustomer(customer.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      {visibleColumns.map((columnId) => (
                        <React.Fragment key={columnId}>
                          {renderColumnCell(columnId, customer)}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>



      {/* Customer Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer?.name} - {selectedCustomer?.accountNumber}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-120px)]">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
                <TabsTrigger value="ageing">Ageing</TabsTrigger>
                <TabsTrigger value="invoices">Invoices</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
                <TabsTrigger value="communications">Communications</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Account Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Account Number</Label>
                        <p className="font-medium">{selectedCustomer?.accountNumber}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Customer Name</Label>
                        <p className="font-medium">{selectedCustomer?.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium">{selectedCustomer?.currency}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium">{selectedCustomer?.contact}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Phone</Label>
                        <p className="font-medium">{selectedCustomer?.phone}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Address</Label>
                        <p className="font-medium">{selectedCustomer?.address}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Collection Strategy</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label>Assigned Strategy</Label>
                        <Select
                          value={editedCollectionStrategyId}
                          onValueChange={setEditedCollectionStrategyId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select strategy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collectionStrategies
                              .filter((s) => s.status === "active")
                              .map((strategy) => (
                                <SelectItem key={strategy.id} value={strategy.id}>
                                  {strategy.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleSaveCollectionStrategy}
                        disabled={
                          editedCollectionStrategyId === selectedCustomer?.collectionStrategyId
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Quick Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <Label className="text-muted-foreground">Outstanding</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer &&
                            formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Overdue</Label>
                        <p className="text-2xl font-bold text-red-600">
                          {selectedCustomer &&
                            formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">DSO</Label>
                        <p className="text-2xl font-bold">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Financial Tab */}
              <TabsContent value="financial" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Outstanding Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Overdue Amount</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Credit Limit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Available Credit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">
                        {selectedCustomer &&
                          formatCurrency(
                            selectedCustomer.creditLimit - selectedCustomer.outstanding,
                            selectedCustomer.currency
                          )}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Terms & Performance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Days Sales Outstanding</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Credit Utilization</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}{" "}
                        of{" "}
                        {selectedCustomer &&
                          formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                      </span>
                      <span className="font-medium">
                        {selectedCustomer &&
                          Math.round(
                            (selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100
                          )}
                        %
                      </span>
                    </div>
                    <Progress
                      value={
                        selectedCustomer
                          ? (selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100
                          : 0
                      }
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ageing Tab */}
              <TabsContent value="ageing" className="space-y-4">
                {selectedCustomer && (() => {
                  const aging = calculateAging(selectedCustomer);
                  const total = aging.current + aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;
                  const overdue = aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;

                  return (
                    <>
                      <Card>
                        <CardHeader>
                          <CardTitle>Aging Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Period</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="text-right">Percentage</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell>Current (Not Due)</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(aging.current, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {total > 0 ? Math.round((aging.current / total) * 100) : 0}%
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>1-30 Days</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(aging.days1_30, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {total > 0 ? Math.round((aging.days1_30 / total) * 100) : 0}%
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>31-60 Days</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(aging.days31_60, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {total > 0 ? Math.round((aging.days31_60 / total) * 100) : 0}%
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>61-90 Days</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(aging.days61_90, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {total > 0 ? Math.round((aging.days61_90 / total) * 100) : 0}%
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="text-red-600">90+ Days</TableCell>
                                <TableCell className="text-right text-red-600">
                                  {formatCurrency(aging.days90Plus, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right text-red-600">
                                  {total > 0 ? Math.round((aging.days90Plus / total) * 100) : 0}%
                                </TableCell>
                              </TableRow>
                              <TableRow className="font-bold">
                                <TableCell>Total Outstanding</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(total, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell className="text-right">100%</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>

                      <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Current & Not Overdue</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-3xl font-bold text-green-600">
                              {formatCurrency(aging.current, selectedCustomer.currency)}
                            </p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Overdue (30+ Days)</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-3xl font-bold text-red-600">
                              {formatCurrency(overdue, selectedCustomer.currency)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Unpaid Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockInvoices[selectedCustomer.id] || []).map((invoice) => {
                            const today = new Date();
                            const dueDate = new Date(invoice.dueDate);
                            const daysOverdue = Math.floor(
                              (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
                            );

                            return (
                              <TableRow key={invoice.id}>
                                <TableCell className="font-medium">{invoice.id}</TableCell>
                                <TableCell>{formatDate(invoice.date)}</TableCell>
                                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(invoice.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      invoice.status === "overdue" ? "destructive" : "secondary"
                                    }
                                  >
                                    {invoice.status}
                                  </Badge>
                                </TableCell>
                                <TableCell
                                  className={`text-right ${daysOverdue > 0 ? "text-red-600" : ""}`}
                                >
                                  {daysOverdue > 0 ? daysOverdue : 0}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Payment History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockPayments[selectedCustomer.id] || []).map((payment) => (
                            <>
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => togglePaymentExpansion(payment.id)}
                                  >
                                    {expandedPayments.includes(payment.id) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-medium">{payment.id}</TableCell>
                                <TableCell>{formatDate(payment.date)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(payment.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>{payment.method}</TableCell>
                                <TableCell>{payment.reference}</TableCell>
                              </TableRow>
                              {expandedPayments.includes(payment.id) && (
                                <TableRow>
                                  <TableCell colSpan={6} className="bg-muted/50">
                                    <div className="p-4">
                                      <p className="mb-2 font-semibold">Invoice Allocations:</p>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Invoice #</TableHead>
                                            <TableHead>Invoice Date</TableHead>
                                            <TableHead className="text-right">
                                              Allocated Amount
                                            </TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {payment.allocations.map((allocation, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell>{allocation.invoiceId}</TableCell>
                                              <TableCell>
                                                {formatDate(allocation.invoiceDate)}
                                              </TableCell>
                                              <TableCell className="text-right">
                                                {formatCurrency(
                                                  allocation.amount,
                                                  selectedCustomer.currency
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-muted-foreground">Total Payments (Last 3 Months)</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer &&
                            formatCurrency(
                              (mockPayments[selectedCustomer.id] || []).reduce(
                                (sum, p) => sum + p.amount,
                                0
                              ),
                              selectedCustomer.currency
                            )}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment Date</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Communications Tab */}
              <TabsContent value="communications">
                {selectedCustomer && (
                  <CommunicationHistoryTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                  />
                )}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts">
                {selectedCustomer && (
                  <AllContactsTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                  />
                )}
              </TabsContent>
            </Tabs>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={sendEmailDialog} onOpenChange={setSendEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>Send an email to the customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={selectedCustomer_floatingMenu?.contact || ""} disabled />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmailSubmit}>Send Email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Call Dialog */}
      <Dialog open={logCallDialog} onOpenChange={setLogCallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>Record details of your call with the customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer</Label>
              <Input value={selectedCustomer_floatingMenu?.name || ""} disabled />
            </div>
            <div>
              <Label>Contact</Label>
              <Input value={selectedCustomer_floatingMenu?.contact || ""} disabled />
            </div>
            <div>
              <Label>Call Outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment_promised">Payment Promised</SelectItem>
                  <SelectItem value="dispute_raised">Dispute Raised</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="left_voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="payment_plan_agreed">Payment Plan Agreed</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Call Notes *</Label>
              <Textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={5}
                placeholder="Enter call notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogCallDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogCallSubmit}>Save Call Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialog} onOpenChange={setAddCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Create a new customer account</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={newCustomer.accountNumber}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, accountNumber: e.target.value })
                  }
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={newCustomer.contact}
                  onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label>Address *</Label>
                <Textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  placeholder="Enter address"
                  rows={3}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={newCustomer.currency}
                  onValueChange={(value: any) => setNewCustomer({ ...newCustomer, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Limit *</Label>
                <Input
                  type="number"
                  value={newCustomer.creditLimit}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, creditLimit: e.target.value })
                  }
                  placeholder="Enter credit limit"
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={newCustomer.paymentTerms}
                  onValueChange={(value: any) =>
                    setNewCustomer({ ...newCustomer, paymentTerms: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                    <SelectItem value="Net 90">Net 90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Collection Strategy (Optional)</Label>
                <Select
                  value={newCustomer.collectionStrategyId}
                  onValueChange={(value) =>
                    setNewCustomer({ ...newCustomer, collectionStrategyId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {collectionStrategies
                      .filter((s) => s.status === "active")
                      .map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          {strategy.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustomerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCustomer}>Add Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* More Filters Sheet */}
      <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Advanced Filters</SheetTitle>
            <SheetDescription>
              Filter customers by multiple criteria
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-180px)] pr-4 pl-4 mt-6">
            <div className="space-y-4">
              {/* Currency Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Currency</Label>
                <MultiSelect
                  options={["USD", "EUR", "GBP", "CAD", "AUD"].map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={advancedFilters.currencies}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      currencies: values,
                    })
                  }
                />
              </div>

              {/* Payment Terms Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Payment Terms</Label>
                <MultiSelect
                  options={["Net 15", "Net 30", "Net 45", "Net 60", "Net 90"].map((term) => ({
                    value: term,
                    label: term,
                  }))}
                  placeholder="Select payment terms..."
                  emptyMessage="No payment terms found."
                  value={advancedFilters.paymentTerms}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      paymentTerms: values,
                    })
                  }
                />
              </div>

              {/* Collection Strategy Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Collection Strategy</Label>
                <MultiSelect
                  options={collectionStrategies
                    .filter((s) => s.status === "active")
                    .map((strategy) => ({
                      value: strategy.id,
                      label: strategy.name,
                    }))}
                  placeholder="Select collection strategies..."
                  emptyMessage="No strategies found."
                  value={advancedFilters.collectionStrategies}
                  onValueChange={(values) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      collectionStrategies: values,
                    })
                  }
                />
              </div>

              <Separator />

              {/* Outstanding Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Outstanding Balance</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.outstandingMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          outstandingMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.outstandingMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          outstandingMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Overdue Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Overdue Balance</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.overdueMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          overdueMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.overdueMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          overdueMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Credit Limit Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Credit Limit</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.creditLimitMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          creditLimitMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.creditLimitMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          creditLimitMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* DSO Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Days Sales Outstanding (DSO)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.dsoMin}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          dsoMin: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={advancedFilters.dsoMax}
                      onChange={(e) =>
                        setAdvancedFilters({
                          ...advancedFilters,
                          dsoMax: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setAdvancedFilters({
                    currencies: [],
                    paymentTerms: [],
                    collectionStrategies: [],
                    outstandingMin: "",
                    outstandingMax: "",
                    overdueMin: "",
                    overdueMax: "",
                    creditLimitMin: "",
                    creditLimitMax: "",
                    dsoMin: "",
                    dsoMax: "",
                  });
                }}
              >
                Clear All
              </Button>
              <Button
                className="flex-1"
                onClick={() => setMoreFiltersOpen(false)}
              >
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        visibleColumns={visibleColumns}
        onColumnsChange={setVisibleColumns}
      />
    </div>
  );
}
